import { useState, useCallback, useEffect } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, XCircle, RefreshCw, Plus, Trash2, Clock } from 'lucide-react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const COLORS = ['#0E7490', '#7C3AED', '#059669', '#DC2626', '#D97706', '#2563EB', '#DB2777'];
const HOURS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MINUTES = ['00','15','30','45'];

const STATUS_BADGE_CLASSES = {
  scheduled: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  completed: 'bg-green-50 text-green-700 border-green-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_BG = { completed: '#059669', cancelled: '#94a3b8' };

function getDayClass(isToday, isPast) {
  if (isToday) return 'bg-[#1E1B4B]/5 border-[#1E1B4B]';
  if (isPast) return 'bg-slate-50/50 border-transparent';
  return 'border-slate-100 hover:border-slate-200';
}

function getDayTextClass(isToday, isPast) {
  if (isToday) return 'text-[#1E1B4B]';
  if (isPast) return 'text-slate-300';
  return 'text-slate-600';
}

export default function AdminSchedulesTab({ allSchedules, onMarkComplete, onRefresh }) {
  const [view, setView] = useState('list');
  const [month, setMonth] = useState(new Date());
  const [cancelDialog, setCancelDialog] = useState(null);
  const [rescheduleDialog, setRescheduleDialog] = useState(null);
  const [addDateDialog, setAddDateDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [newDate, setNewDate] = useState('');
  const [reschedTime, setReschedTime] = useState({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
  const [addDate, setAddDate] = useState('');
  const [addTime, setAddTime] = useState({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });

  const todayStr = new Date().toISOString().split('T')[0];
  const instructorNames = [...new Set(allSchedules.map(s => s.instructor_name))];
  const instructorColor = {};
  instructorNames.forEach((n, i) => { instructorColor[n] = COLORS[i % COLORS.length]; });

  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      await api.put(`/schedules/${cancelDialog.schedule_id}/entries/${cancelDialog.entry_id}/cancel`, { reason: cancelReason });
      toast.success('Session cancelled — instructor and parent notified');
      setCancelDialog(null);
      setCancelReason('');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to cancel'); }
  };

  const handleReschedule = async () => {
    if (!rescheduleDialog || !newDate) { toast.error('Select a new date'); return; }
    const { startHour: sh, startMinute: sm, startPeriod: sp, endHour: eh, endMinute: em, endPeriod: ep } = reschedTime;
    const timeSlot = (sh && eh) ? `${sh}:${sm} ${sp} - ${eh}:${em} ${ep}` : '';
    try {
      await api.put(`/schedules/${rescheduleDialog.schedule_id}/entries/${rescheduleDialog.entry_id}/reschedule`, {
        new_date: newDate, new_time: timeSlot,
      });
      toast.success('Session rescheduled — instructor and parent notified');
      setRescheduleDialog(null);
      setNewDate('');
      setReschedTime({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to reschedule'); }
  };

  const handleAddDate = async () => {
    if (!addDateDialog || !addDate) { toast.error('Select a date'); return; }
    const { startHour: sh, startMinute: sm, startPeriod: sp, endHour: eh, endMinute: em, endPeriod: ep } = addTime;
    const timeSlot = (sh && eh) ? `${sh}:${sm} ${sp} - ${eh}:${em} ${ep}` : '';
    try {
      await api.put(`/schedules/${addDateDialog.schedule_id}`, { add_dates: [addDate] });
      // If a custom time was provided, update the new entry's time
      if (timeSlot) {
        const updated = await api.get(`/schedules/${addDateDialog.schedule_id}`);
        const newEntry = updated.data.entries?.find(e => e.date === addDate);
        if (newEntry) {
          const entries = updated.data.entries.map(e => e.entry_id === newEntry.entry_id ? { ...e, time_slot: timeSlot } : e);
          await api.put(`/schedules/${addDateDialog.schedule_id}`, { entries });
        }
      }
      toast.success('Session added — instructor and parent notified');
      setAddDateDialog(null);
      setAddDate('');
      setAddTime({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to add session'); }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this entire schedule? This cannot be undone.')) return;
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleDeleteEntry = async (scheduleId, entryId) => {
    if (!window.confirm('Remove this session from the schedule?')) return;
    try {
      await api.delete(`/schedules/${scheduleId}/entries/${entryId}`);
      toast.success('Session removed');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to delete session'); }
  };

  // Calendar view logic
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const days = new Date(year, m + 1, 0).getDate();
  const calGrid = [];
  for (let i = 0; i < firstDay; i++) calGrid.push(null);
  for (let d = 1; d <= days; d++) {
    calGrid.push(`${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }

  const dateMap = {};
  allSchedules.forEach(s => {
    (s.entries || []).forEach(e => {
      if (!dateMap[e.date]) dateMap[e.date] = [];
      dateMap[e.date].push({ ...e, child_name: s.child_name, instructor_name: s.instructor_name, schedule_id: s.schedule_id, time_slot: e.time_slot || s.time_slot });
    });
  });

  return (
    <div className="space-y-4" data-testid="admin-schedules-tab">
      <div className="flex items-center gap-3">
        <Button variant={view === 'calendar' ? 'default' : 'outline'} size="sm" className="rounded-full text-xs font-bold" onClick={() => setView('calendar')}>Calendar</Button>
        <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" className="rounded-full text-xs font-bold" onClick={() => setView('list')}>List</Button>
      </div>

      {view === 'calendar' ? (
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="icon" onClick={() => setMonth(new Date(year, m - 1))}><ChevronLeft className="w-4 h-4" /></Button>
              <h3 className="text-lg font-bold text-slate-700" style={{ fontFamily: 'Nunito' }}>{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
              <Button variant="ghost" size="icon" onClick={() => setMonth(new Date(year, m + 1))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {DAY_NAMES.map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 py-2">{d}</div>)}
              {calGrid.map((dateStr, i) => {
                if (!dateStr) return <div key={`e-${i}`} />;
                const day = parseInt(dateStr.split('-')[2]);
                const entries = dateMap[dateStr] || [];
                const isToday = dateStr === todayStr;
                const isPast = dateStr < todayStr;
                return (
                  <div key={dateStr} className={`relative p-1 rounded-xl min-h-[70px] text-xs transition-colors border ${getDayClass(isToday, isPast)}`}>
                    <span className={`font-bold ${getDayTextClass(isToday, isPast)}`}>{day}</span>
                    <div className="mt-0.5 space-y-0.5">
                      {entries.slice(0, 3).map((e) => (
                        <div key={e.entry_id} className={`px-1 py-0.5 rounded text-white text-[10px] leading-tight truncate font-medium ${e.status === 'cancelled' ? 'line-through opacity-50' : ''}`} style={{ background: STATUS_BG[e.status] || instructorColor[e.instructor_name] || '#64748B' }} title={`${e.child_name} - ${e.instructor_name} ${e.time_slot || ''} (${e.status})`}>
                          {e.child_name}
                        </div>
                      ))}
                      {entries.length > 3 && <div className="text-[10px] text-slate-400 font-medium">+{entries.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        allSchedules.length === 0 ? (
          <Card className="rounded-3xl border-dashed border-2 border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <CalendarDays className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Schedules</p>
            </CardContent>
          </Card>
        ) : (
          allSchedules.map(schedule => {
            const upcoming = schedule.entries.filter(e => e.date >= todayStr);
            const scheduledCount = upcoming.filter(e => e.status === 'scheduled').length;
            const completedCount = schedule.entries.filter(e => e.status === 'completed').length;
            return (
              <Card key={schedule.schedule_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`admin-schedule-${schedule.schedule_id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-col gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ background: instructorColor[schedule.instructor_name] || '#64748B' }} />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{schedule.child_name}</h3>
                        <p className="text-xs text-slate-500 truncate">Instructor: {schedule.instructor_name} {schedule.time_slot && `| ${schedule.time_slot}`}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <Badge variant="outline" className="rounded-full text-xs bg-cyan-50 border-cyan-200 text-cyan-700">{scheduledCount} upcoming</Badge>
                      <Badge variant="outline" className="rounded-full text-xs bg-green-50 border-green-200 text-green-700">{completedCount} done</Badge>
                      <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 h-8 border-[#22D3EE] text-[#0E7490]" onClick={() => setAddDateDialog({ schedule_id: schedule.schedule_id, child_name: schedule.child_name })} data-testid={`add-date-${schedule.schedule_id}`}>
                        <Plus className="w-3 h-3" /> Add
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-xs text-red-400 hover:text-red-600 h-8 w-8 p-0" onClick={() => handleDeleteSchedule(schedule.schedule_id)} data-testid={`delete-schedule-${schedule.schedule_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-auto">
                    {upcoming.map(entry => (
                      <div key={entry.entry_id} className="flex flex-col gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`admin-entry-${entry.entry_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                            {new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          {entry.time_slot && <span className="text-xs text-slate-400 whitespace-nowrap">{entry.time_slot}</span>}
                          <Badge variant="outline" className={`rounded-full text-xs capitalize ${STATUS_BADGE_CLASSES[entry.status] || 'bg-amber-50 text-amber-700 border-amber-200'}`}>{entry.status}</Badge>
                        </div>
                        {entry.status === 'scheduled' && (
                          <div className="flex gap-1.5 flex-wrap">
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-green-200 text-green-600 hover:bg-green-50 h-8" onClick={() => onMarkComplete(schedule.schedule_id, entry.entry_id)} data-testid={`complete-entry-${entry.entry_id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Done
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-amber-200 text-amber-600 hover:bg-amber-50 h-8" onClick={() => { setRescheduleDialog({ schedule_id: schedule.schedule_id, entry_id: entry.entry_id, child_name: schedule.child_name, old_date: entry.date }); setNewDate(''); setReschedTime({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' }); }} data-testid={`reschedule-entry-${entry.entry_id}`}>
                              <RefreshCw className="w-3 h-3" /> Move
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-red-200 text-red-500 hover:bg-red-50 h-8" onClick={() => { setCancelDialog({ schedule_id: schedule.schedule_id, entry_id: entry.entry_id, child_name: schedule.child_name, date: entry.date }); setCancelReason(''); }} data-testid={`cancel-entry-${entry.entry_id}`}>
                              <XCircle className="w-3 h-3" /> Cancel
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-red-300 text-red-600 hover:bg-red-50 h-8" onClick={() => handleDeleteEntry(schedule.schedule_id, entry.entry_id)} data-testid={`delete-entry-${entry.entry_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )
      )}

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" /> Cancel Session
            </DialogTitle>
          </DialogHeader>
          {cancelDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-600">Cancel <strong>{cancelDialog.child_name}</strong>&apos;s session on <strong>{cancelDialog.date}</strong>? Both the instructor and parent will be notified.</p>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason (optional)" className="rounded-xl" rows={2} data-testid="cancel-reason-input" />
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="rounded-full font-bold" onClick={() => setCancelDialog(null)}>Keep</Button>
                <Button className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={handleCancel} data-testid="confirm-cancel-btn">
                  <XCircle className="w-4 h-4" /> Cancel Session
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleDialog} onOpenChange={() => setRescheduleDialog(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" /> Reschedule Session
            </DialogTitle>
          </DialogHeader>
          {rescheduleDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-600">Move <strong>{rescheduleDialog.child_name}</strong>&apos;s session from <strong>{rescheduleDialog.old_date}</strong> to a new date. Both the instructor and parent will be notified.</p>
              <div>
                <label className="text-sm font-semibold text-slate-600">New Date</label>
                <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="rounded-xl mt-1" min={todayStr} data-testid="reschedule-date-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">New Time</label>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Select value={reschedTime.startHour} onValueChange={v => setReschedTime(t => ({...t, startHour: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="admin-resched-start-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-slate-400 font-bold text-xs">:</span>
                    <Select value={reschedTime.startMinute} onValueChange={v => setReschedTime(t => ({...t, startMinute: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={reschedTime.startPeriod} onValueChange={v => setReschedTime(t => ({...t, startPeriod: v}))}>
                      <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <span className="text-slate-400 font-bold text-xs">to</span>
                  <div className="flex items-center gap-1">
                    <Select value={reschedTime.endHour} onValueChange={v => setReschedTime(t => ({...t, endHour: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="admin-resched-end-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-slate-400 font-bold text-xs">:</span>
                    <Select value={reschedTime.endMinute} onValueChange={v => setReschedTime(t => ({...t, endMinute: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={reschedTime.endPeriod} onValueChange={v => setReschedTime(t => ({...t, endPeriod: v}))}>
                      <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="rounded-full font-bold" onClick={() => setRescheduleDialog(null)}>Cancel</Button>
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2" onClick={handleReschedule} data-testid="confirm-reschedule-btn">
                  <RefreshCw className="w-4 h-4" /> Reschedule
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Date Dialog */}
      <Dialog open={!!addDateDialog} onOpenChange={() => setAddDateDialog(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#22D3EE]" /> Add Session Date
            </DialogTitle>
          </DialogHeader>
          {addDateDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-600">Add a new session date for <strong>{addDateDialog.child_name}</strong>. Both the instructor and parent will be notified.</p>
              <div>
                <label className="text-sm font-semibold text-slate-600">Date</label>
                <Input type="date" value={addDate} onChange={e => setAddDate(e.target.value)} className="rounded-xl mt-1" min={todayStr} data-testid="add-date-input" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Time Slot</label>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Select value={addTime.startHour} onValueChange={v => setAddTime(t => ({...t, startHour: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="admin-add-start-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-slate-400 font-bold text-xs">:</span>
                    <Select value={addTime.startMinute} onValueChange={v => setAddTime(t => ({...t, startMinute: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={addTime.startPeriod} onValueChange={v => setAddTime(t => ({...t, startPeriod: v}))}>
                      <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <span className="text-slate-400 font-bold text-xs">to</span>
                  <div className="flex items-center gap-1">
                    <Select value={addTime.endHour} onValueChange={v => setAddTime(t => ({...t, endHour: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="admin-add-end-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-slate-400 font-bold text-xs">:</span>
                    <Select value={addTime.endMinute} onValueChange={v => setAddTime(t => ({...t, endMinute: v}))}>
                      <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={addTime.endPeriod} onValueChange={v => setAddTime(t => ({...t, endPeriod: v}))}>
                      <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="rounded-full font-bold" onClick={() => setAddDateDialog(null)}>Cancel</Button>
                <Button className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" onClick={handleAddDate} data-testid="confirm-add-date-btn">
                  <Plus className="w-4 h-4" /> Add Date
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
